import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Plus, Edit2, Trash2, RotateCcw, Save, X, Eye, EyeOff, Loader, Key, Copy, Check, Mail, Send, Clock } from 'lucide-react';
import { neonQuery } from '../services/api';

// adm_users.is_active is legacy TEXT carrying four shapes of yes/no: '1', 'true', '0' and NULL.
// This column used to be read here with plain JS truthiness, and '0' is a NON-EMPTY STRING, so the
// one row that matters - the person who genuinely cannot log in - rendered as a green Active badge.
// Zachary Paul sat at is_active = '0' showing Active while every sign-in attempt died, which is what
// sent two people hunting an MFA bug. Mirrors isActiveFlag() in netlify/functions/neon.js; keep the
// two in step.
const isActiveFlag = (v) => ['1', 'true', 't', 'yes', 'y'].includes(String(v == null ? '' : v).trim().toLowerCase());


const AVAILABLE_ROLES = ['Admin', 'IT', 'Manager', 'Procurement', 'Fulfillment', 'Customer Service', 'Marketing', 'HR', 'Accounting', 'Seventh Street'];
const ROLE_COLORS = { Admin: '#00395C', IT: '#7c3aed', Manager: '#0e7490', Procurement: '#6b21a8', Fulfillment: '#0369a1', 'Customer Service': '#15803d', Marketing: '#DC582A', HR: '#b45309', Accounting: '#4338ca' };
const INPUT = { padding: '7px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const BTN = (bg, disabled) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 14px',
  background: disabled ? 'var(--gray-300)' : bg, color: '#fff', border: 'none', borderRadius: 6,
  fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
});

// Tier 3 #3 — live password complexity feedback for admin user create/edit form.
function PasswordRequirements({ pwd, username }) {
  const checks = [
    { ok: (pwd || '').length >= 12, label: 'At least 12 characters' },
    { ok: !username || !(pwd || '').toLowerCase().includes(String(username).toLowerCase()), label: 'Does not contain username' },
  ];
  return (
    <div style={{ marginTop: 6, fontSize: 10, lineHeight: 1.5 }}>
      {checks.map((c, i) => (
        <div key={i} style={{ color: c.ok ? '#16a34a' : 'var(--gray-500)' }}>
          {c.ok ? '✓' : '○'} {c.label}
        </div>
      ))}
    </div>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // admin_id or 'new'
  const [form, setForm] = useState({ staff_name: '', usern: '', pwd: '', email: '', is_customer_service: false, is_active: true });
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [setupCode, setSetupCode] = useState(null); // { adminId, code, emailSent, email }
  const [codeCopied, setCodeCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null); // admin_id being sent
  const [modalEmailStatus, setModalEmailStatus] = useState(null); // { ok, msg } for inline modal feedback

  const generateSetupCode = async (adminId, sendEmail = false) => {
    if (sendEmail) setSendingEmail(adminId);
    try {
      const data = await neonQuery('auth_create_setup_code', { adminId, sendEmail });
      if (data?.code) {
        setSetupCode({ adminId, code: data.code, emailSent: data.emailSent, email: data.email });
        setCodeCopied(false);
      }
    } catch (e) { setError(e.message); }
    finally { setSendingEmail(null); }
  };

  const sendAllSetupEmails = async () => {
    setSendingEmail('all');
    let unenrolled = [];
    try {
      // Phase 4c: skip users who already have a passkey so daily re-runs don't spam them.
      unenrolled = await neonQuery('users_without_passkeys');
    } catch (e) {
      setError('Could not check enrollment status: ' + e.message);
      setSendingEmail(null);
      return;
    }
    const targets = (unenrolled || []).filter(u => u.email);
    const skippedNoEmail = (unenrolled || []).length - targets.length;
    let sent = 0;
    for (const user of targets) {
      try {
        await neonQuery('auth_create_setup_code', { adminId: user.admin_id, sendEmail: true });
        sent++;
      } catch(e) { /* continue */ }
    }
    setSetupCode({ adminId: null, code: null, emailSent: true, bulkCount: sent, bulkSkippedNoEmail: skippedNoEmail, bulkTotalUnenrolled: (unenrolled || []).length });
    setSendingEmail(null);
  };

  const copyCode = () => {
    if (setupCode?.code) {
      navigator.clipboard.writeText(setupCode.code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 3000);
    }
  };

  // Phase 4 — per-credential view + revoke. The modal lists every webauthn_credentials
  // row for a user; the admin can revoke one without touching the others.
  const [passkeyModal, setPasskeyModal] = useState(null); // { adminId, staffName, rows, loading, error, revokingPrefix }
  // Tier 2 — per-session revocation modal
  const [sessionsModal, setSessionsModal] = useState(null); // { adminId, staffName, rows, loading, error, revokingToken }
  const openPasskeyModal = async (user) => {
    setPasskeyModal({ adminId: user.admin_id, staffName: user.staff_name, rows: [], loading: true, error: null, revokingPrefix: null });
    try {
      const rows = await neonQuery('webauthn_credentials_for_admin', { adminId: user.admin_id });
      setPasskeyModal({ adminId: user.admin_id, staffName: user.staff_name, rows: rows || [], loading: false, error: null, revokingPrefix: null });
    } catch (e) {
      setPasskeyModal((m) => m && { ...m, loading: false, error: e.message });
    }
  };
  const revokeCredential = async (cred) => {
    if (!passkeyModal) return;
    const fullId = cred.id;
    if (!fullId) {
      setPasskeyModal((m) => m && { ...m, error: 'Credential ID missing on this row. Reload and retry.' });
      return;
    }
    if (!window.confirm(`Revoke this passkey for ${passkeyModal.staffName}? They'll need a new setup code to sign in on that device again.`)) return;
    setPasskeyModal((m) => m && { ...m, revokingPrefix: cred.id_prefix, error: null });
    try {
      await neonQuery('webauthn_credential_revoke', { credentialId: fullId });
      // Refresh list + the main user table (passkey_count changes).
      const rows = await neonQuery('webauthn_credentials_for_admin', { adminId: passkeyModal.adminId });
      setPasskeyModal((m) => m && { ...m, rows: rows || [], revokingPrefix: null });
      loadUsers();
    } catch (e) {
      setPasskeyModal((m) => m && { ...m, revokingPrefix: null, error: e.message });
    }
  };

  const openSessionsModal = async (user) => {
    setSessionsModal({ adminId: user.admin_id, staffName: user.staff_name, rows: [], loading: true, error: null, revokingToken: null });
    try {
      const rows = await neonQuery('sessions_for_admin', { adminId: user.admin_id });
      setSessionsModal({ adminId: user.admin_id, staffName: user.staff_name, rows: rows || [], loading: false, error: null, revokingToken: null });
    } catch (e) {
      setSessionsModal((m) => m && { ...m, loading: false, error: e.message });
    }
  };
  const revokeSession = async (sess) => {
    if (!sessionsModal) return;
    const fullToken = sess.token;
    if (!fullToken) { setSessionsModal((m) => m && { ...m, error: 'Session token missing.' }); return; }
    if (!window.confirm(`Revoke this session for ${sessionsModal.staffName}? They'll be signed out everywhere using that session.`)) return;
    setSessionsModal((m) => m && { ...m, revokingToken: sess.token_prefix, error: null });
    try {
      await neonQuery('session_revoke', { token: fullToken });
      const rows = await neonQuery('sessions_for_admin', { adminId: sessionsModal.adminId });
      setSessionsModal((m) => m && { ...m, rows: rows || [], revokingToken: null });
    } catch (e) {
      setSessionsModal((m) => m && { ...m, revokingToken: null, error: e.message });
    }
  };

  const loadUsers = useCallback(async () => {
    try {
      const rows = await neonQuery('users_list');
      setUsers(rows);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const startAdd = () => {
    setEditing('new');
    setForm({ staff_name: '', usern: '', pwd: '', email: '', is_customer_service: false, is_active: true, roles: [] });
    setError('');
    setModalEmailStatus(null);
    setShowPwd(false);
    setPwdTouched(true);
  };

  const [pwdTouched, setPwdTouched] = useState(false);

  const formRef = useRef(null);

  const startEdit = (user) => {
    setEditing(user.admin_id);
    const userRoles = (user.roles || '').split(',').map(r => r.trim()).filter(Boolean);
    setForm({ staff_name: user.staff_name || '', usern: user.usern, pwd: user.pwd || '', email: user.email || '', is_customer_service: user.is_customer_service || false, is_active: isActiveFlag(user.is_active), roles: userRoles });
    setError('');
    setModalEmailStatus(null);
    setShowPwd(false);
    setPwdTouched(false);
  };

  const cancel = () => { setEditing(null); setError(''); setModalEmailStatus(null); };

  // Esc to close + body scroll lock while modal is open
  useEffect(() => {
    if (editing === null) return;
    const onKey = (e) => { if (e.key === 'Escape') cancel(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [editing]);

  const resendFromModal = async () => {
    if (!editing || editing === 'new' || !form.email?.trim()) return;
    setModalEmailStatus(null);
    setSendingEmail(editing);
    try {
      const data = await neonQuery('auth_create_setup_code', { adminId: editing, sendEmail: true });
      if (data?.emailSent) {
        setModalEmailStatus({ ok: true, msg: `Setup email sent to ${data.email || form.email}. Link expires in 24 hours.` });
      } else {
        setModalEmailStatus({ ok: false, msg: data?.error || 'Email did not send. Check RESEND_API_KEY on Netlify.' });
      }
    } catch (e) {
      setModalEmailStatus({ ok: false, msg: e.message || 'Failed to send setup email.' });
    } finally {
      setSendingEmail(null);
    }
  };

  const save = async () => {
    setError('');
    if (!form.staff_name.trim() || !form.usern.trim()) {
      setError('Name and username are required.');
      return;
    }
    if (editing === 'new' && !form.pwd) {
      setError('Password is required for new users.');
      return;
    }
    // Tier 3 #3 — client-side complexity preflight (server enforces too).
    if (form.pwd && (form.pwd.length < 12 || (form.usern && form.pwd.toLowerCase().includes(form.usern.toLowerCase())))) {
      setError('Password must be at least 12 characters and not contain the username.');
      return;
    }
    try {
      let rows;
      if (editing === 'new') {
        rows = await neonQuery('users_add', {
          staff_name: form.staff_name.trim(),
          usern: form.usern.trim(),
          pwd: form.pwd,
          email: form.email.trim(),
          is_customer_service: form.is_customer_service,
          is_active: form.is_active,
          roles: form.roles.join(','),
        });
      } else {
        rows = await neonQuery('users_update', {
          admin_id: editing,
          staff_name: form.staff_name.trim(),
          usern: form.usern.trim(),
          pwd: pwdTouched ? form.pwd : undefined,
          email: form.email.trim(),
          is_customer_service: form.is_customer_service,
          is_active: form.is_active,
          roles: form.roles.join(','),
        });
      }
      setUsers(rows);
      setEditing(null);
    } catch (e) {
      setError(e.message || 'Failed to save user.');
    }
  };

  const softDelete = async (id) => {
    try {
      const rows = await neonQuery('users_delete', { admin_id: id });
      setUsers(rows);
      setEditing(null);
    } catch (e) { setError(e.message); }
  };

  const restore = async (id) => {
    try {
      const rows = await neonQuery('users_restore', { admin_id: id });
      setUsers(rows);
    } catch (e) { setError(e.message); }
  };

  const activeUsers = users.filter(u => !u.deleted_at);
  const deletedUsers = users.filter(u => u.deleted_at);

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Users size={24} color="var(--navy)" />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>User Management</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={sendAllSetupEmails} disabled={sendingEmail === 'all'}
            style={BTN('var(--orange)', sendingEmail === 'all')}>
            <Mail size={14} /> {sendingEmail === 'all' ? 'Sending...' : 'Send All Setup Emails'}
          </button>
          <button onClick={startAdd} style={BTN('var(--navy)', false)}>
            <Plus size={14} /> Add User
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {editing !== null && (
        <div
          onClick={cancel}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '60px 20px 20px', zIndex: 1000, overflowY: 'auto',
          }}
        >
        <div ref={formRef} onClick={(e) => e.stopPropagation()} style={{ background: '#fff', border: '2px solid var(--navy)', borderRadius: 8, padding: 20, width: '100%', maxWidth: 720, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>
              {editing === 'new' ? 'Add New User' : 'Edit User'}
            </div>
            <button onClick={cancel} title="Close" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: 4 }}>
              <X size={18} />
            </button>
          </div>
          {error && <div style={{ background: '#fee', color: '#c00', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>}
          {modalEmailStatus && (
            <div style={{
              background: modalEmailStatus.ok ? '#eff6ff' : '#fee',
              color: modalEmailStatus.ok ? 'var(--navy)' : '#c00',
              border: `1px solid ${modalEmailStatus.ok ? '#3b82f6' : '#f5c2c2'}`,
              padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Mail size={14} /> {modalEmailStatus.msg}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 4, display: 'block' }}>Full Name *</label>
              <input type="text" value={form.staff_name} onChange={e => setForm({ ...form, staff_name: e.target.value })}
                placeholder="e.g., Matt Cain Jr" style={INPUT} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 4, display: 'block' }}>Username *</label>
              <input type="text" value={form.usern} onChange={e => setForm({ ...form, usern: e.target.value })}
                autoComplete="off" name="usern-edit-field"
                placeholder="e.g., mattjr" style={INPUT} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 4, display: 'block' }}>
                Password {editing !== 'new' && <span style={{ fontWeight: 400, color: '#999' }}>(leave blank to keep current)</span>}
              </label>
              <div style={{ position: 'relative' }}>
                <input type={showPwd ? 'text' : 'password'} value={form.pwd} onChange={e => { setForm({ ...form, pwd: e.target.value }); setPwdTouched(true); }}
                  autoComplete="new-password" name="pwd-edit-field"
                  placeholder={editing === 'new' ? 'Required' : 'Leave blank to keep current'}
                  style={{ ...INPUT, paddingRight: 34 }} />
                <button onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: 8, top: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}>
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {form.pwd && <PasswordRequirements pwd={form.pwd} username={form.usern} />}
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 4, display: 'block' }}>Email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="e.g., matt@crabplace.com" style={INPUT} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 6, display: 'block' }}>Roles</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {AVAILABLE_ROLES.map(role => {
                const active = form.roles.includes(role);
                return (
                  <button key={role} type="button" onClick={() => {
                    setForm(f => ({ ...f, roles: active ? f.roles.filter(r => r !== role) : [...f.roles, role] }));
                  }} style={{
                    padding: '5px 12px', borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: active ? 'none' : '1.5px solid var(--gray-300)',
                    background: active ? (ROLE_COLORS[role] || 'var(--navy)') : '#fff',
                    color: active ? '#fff' : 'var(--gray-600)',
                    fontFamily: 'inherit', transition: 'all 0.15s',
                  }}>
                    {role}
                  </button>
                );
              })}
            </div>
          </div>
          {editing !== 'new' && (
            <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--gray-700)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                Active (can log in)
              </label>
              {!form.is_active && <span style={{ fontSize: 11, color: 'var(--red)' }}>Currently disabled — check the box + Save to let them log in.</span>}
            </div>
          )}
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={save} style={BTN('var(--green)', false)}>
              <Save size={14} /> {editing === 'new' ? 'Create User' : 'Save Changes'}
            </button>
            <button onClick={cancel} style={BTN('var(--gray-500)', false)}>
              <X size={14} /> Cancel
            </button>
            {editing !== 'new' && form.email?.trim() && (
              <button
                onClick={resendFromModal}
                disabled={sendingEmail === editing}
                title={`Send setup/reset link to ${form.email}`}
                style={BTN('var(--orange)', sendingEmail === editing)}
              >
                <Send size={14} /> {sendingEmail === editing ? 'Sending...' : 'Resend Setup Email'}
              </button>
            )}
            {editing !== 'new' && (
              <button
                onClick={async () => {
                  if (!window.confirm('Reset two-factor authentication for this user? Their current password and passkeys also stop working. They come back through Resend Setup Email, Forgot Password, or a new password you set here.')) return;
                  try {
                    await neonQuery('mfa_admin_reset', { adminId: editing });
                    setModalEmailStatus({ ok: true, msg: 'MFA reset, old password and passkeys cancelled. Send the setup email, or enter a new password and save.' });
                    loadUsers();
                  } catch (e) { setModalEmailStatus({ ok: false, msg: 'Reset failed: ' + e.message }); }
                }}
                style={{ ...BTN('var(--gray-600)', false) }}
              >
                <Key size={14} /> Reset MFA
              </button>
            )}
            {editing !== 'new' && (
              <button onClick={() => softDelete(editing)} style={{ ...BTN('var(--red)', false), marginLeft: 'auto' }}>
                <Trash2 size={14} /> Deactivate
              </button>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Setup Code / Email Confirmation Banner */}
      {setupCode && (
        <div style={{
          background: setupCode.emailSent ? '#eff6ff' : '#f0fdf4',
          border: `2px solid ${setupCode.emailSent ? '#3b82f6' : 'var(--green)'}`,
          borderRadius: 8, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16,
        }}>
          {setupCode.emailSent ? <Mail size={20} color="#3b82f6" /> : <Key size={20} color="var(--green)" />}
          <div style={{ flex: 1 }}>
            {setupCode.bulkCount !== undefined ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
                  {setupCode.bulkCount === 0
                    ? 'No setup emails needed — everyone with an email is enrolled.'
                    : `Setup emails sent to ${setupCode.bulkCount} unenrolled user${setupCode.bulkCount === 1 ? '' : 's'}`}
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
                  {setupCode.bulkTotalUnenrolled} total without a passkey.
                  {setupCode.bulkSkippedNoEmail ? ` ${setupCode.bulkSkippedNoEmail} skipped (no email on file — use the in-person Key icon).` : ''}
                  {setupCode.bulkCount > 0 ? ' Codes expire in 24 hours.' : ''}
                </div>
              </>
            ) : setupCode.emailSent ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
                  Setup email sent to {setupCode.email}
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
                  They'll receive a link to set up their passkey. Code expires in 24 hours.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>
                  Passkey Setup Code for {users.find(u => u.admin_id === setupCode.adminId)?.staff_name || 'User'}
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 6, fontFamily: 'monospace', color: 'var(--navy)' }}>
                  {setupCode.code}
                </div>
                <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>
                  Expires in 30 minutes. Have the user enter this code on the login screen.
                </div>
              </>
            )}
          </div>
          {setupCode.code && !setupCode.emailSent && (
            <button onClick={copyCode} style={{ ...BTN(codeCopied ? 'var(--green)' : 'var(--navy)', false), whiteSpace: 'nowrap' }}>
              {codeCopied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Code</>}
            </button>
          )}
          <button onClick={() => setSetupCode(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4 }}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={16} className="spin" /> Loading users...</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid var(--gray-200)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Name</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Username</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Email</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Roles</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: 'var(--gray-700)' }}>Status</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: 'var(--gray-700)' }}>Passkey</th>
                <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: 'var(--gray-700)' }}>MFA</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--gray-700)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeUsers.map(user => (
                <tr key={user.admin_id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{user.staff_name}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--gray-600)' }}>{user.usern}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--gray-600)' }}>{user.email || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {(user.roles || '').split(',').filter(Boolean).map(role => (
                        <span key={role} style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                          background: ROLE_COLORS[role.trim()] || 'var(--gray-600)', color: '#fff' }}>
                          {role.trim()}
                        </span>
                      ))}
                      {!(user.roles || '').trim() && <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>No roles</span>}
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                      background: isActiveFlag(user.is_active) ? '#e6f4ea' : '#fee', color: isActiveFlag(user.is_active) ? 'var(--green)' : 'var(--red)' }}>
                      {isActiveFlag(user.is_active) ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    {user.passkey_count > 0 ? (
                      <button
                        onClick={() => openPasskeyModal(user)}
                        title={user.last_used ? `Last used: ${new Date(user.last_used).toLocaleDateString()} — click to manage` : 'Click to manage'}
                        style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                          background: '#e6f4ea', color: 'var(--green)', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                        {user.passkey_count} registered
                      </button>
                    ) : (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                        background: '#fff3cd', color: '#856404' }}>
                        Not set up
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    {user.mfa_enabled ? (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                        background: '#e6f4ea', color: 'var(--green)' }}>
                        Enabled
                      </span>
                    ) : (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                        background: '#fff3cd', color: '#856404' }}>
                        Not set up
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <button onClick={() => generateSetupCode(user.admin_id, false)}
                      title="Issue 30-minute in-person setup code (no email)"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy)', padding: 4 }}>
                      <Key size={14} />
                    </button>
                    {user.email && (
                      <button onClick={() => generateSetupCode(user.admin_id, true)}
                        title={`Email setup link to ${user.email}`}
                        disabled={sendingEmail === user.admin_id}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--orange)', padding: 4, opacity: sendingEmail === user.admin_id ? 0.4 : 1 }}>
                        <Send size={14} />
                      </button>
                    )}
                    <button onClick={() => openSessionsModal(user)} title="Active sessions"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-600)', padding: 4 }}>
                      <Clock size={14} />
                    </button>
                                        <button onClick={() => startEdit(user)} title="Edit"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy)', padding: 4 }}>
                      <Edit2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {activeUsers.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)' }}>No users found. Add one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tier 2 — Sessions management modal */}
      {sessionsModal && (
        <div onClick={() => setSessionsModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px 20px', zIndex: 1001, overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', border: '2px solid var(--navy)', borderRadius: 8, padding: 20, width: '100%', maxWidth: 720, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <Clock size={16} color="var(--navy)" />
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginLeft: 8 }}>Active sessions for {sessionsModal.staffName}</div>
              <button onClick={() => setSessionsModal(null)} title="Close" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: 4 }}><X size={18} /></button>
            </div>
            {sessionsModal.error && <div style={{ background: '#fee', color: '#c00', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{sessionsModal.error}</div>}
            {sessionsModal.loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading sessions...</div>
            ) : sessionsModal.rows.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>No active sessions for this user.</div>
            ) : (
              <div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>
                  Revoking a session signs that browser/device out immediately. The user can sign back in with their credentials.
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Token</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Device / UA</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Last active</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Expires</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--gray-700)' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionsModal.rows.map(sess => (
                      <tr key={sess.token_prefix} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                        <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11 }}>{sess.token_prefix}…</td>
                        <td style={{ padding: '8px 10px', color: 'var(--gray-600)', fontSize: 11, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sess.device_info || '(unknown)'}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--gray-600)' }}>{sess.last_active_at ? new Date(sess.last_active_at).toLocaleString() : '—'}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--gray-600)' }}>{sess.expires_at ? new Date(sess.expires_at).toLocaleDateString() : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                          <button onClick={() => revokeSession(sess)} disabled={sessionsModal.revokingToken === sess.token_prefix}
                            style={{ ...BTN('var(--red)', sessionsModal.revokingToken === sess.token_prefix), padding: '4px 10px', fontSize: 11 }}>
                            <Trash2 size={12} /> {sessionsModal.revokingToken === sess.token_prefix ? 'Revoking...' : 'Revoke'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

            {/* Passkey management modal */}
      {passkeyModal && (
        <div
          onClick={() => setPasskeyModal(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '60px 20px 20px', zIndex: 1001, overflowY: 'auto',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', border: '2px solid var(--navy)', borderRadius: 8, padding: 20, width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <Key size={16} color="var(--navy)" />
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginLeft: 8 }}>
                Passkeys for {passkeyModal.staffName}
              </div>
              <button onClick={() => setPasskeyModal(null)} title="Close" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: 4 }}>
                <X size={18} />
              </button>
            </div>
            {passkeyModal.error && (
              <div style={{ background: '#fee', color: '#c00', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{passkeyModal.error}</div>
            )}
            {passkeyModal.loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading credentials...</div>
            ) : passkeyModal.rows.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
                No passkeys registered. Issue a setup code from the user table to enroll one.
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>
                  Revoking a passkey immediately stops it from signing in. The user can re-enroll with a fresh setup code.
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Device</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Last used</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Registered</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--gray-700)' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passkeyModal.rows.map(cred => (
                      <tr key={cred.id_prefix} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                        <td style={{ padding: '8px 10px' }}>{cred.device_name || '(unnamed)'}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--gray-600)' }}>
                          {cred.last_used_at ? new Date(cred.last_used_at).toLocaleString() : 'Never'}
                        </td>
                        <td style={{ padding: '8px 10px', color: 'var(--gray-600)' }}>
                          {cred.created_at ? new Date(cred.created_at).toLocaleDateString() : ''}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                          <button
                            onClick={() => revokeCredential(cred)}
                            disabled={passkeyModal.revokingPrefix === cred.id_prefix}
                            style={{ ...BTN('var(--red)', passkeyModal.revokingPrefix === cred.id_prefix), padding: '4px 10px', fontSize: 11 }}>
                            <Trash2 size={12} /> {passkeyModal.revokingPrefix === cred.id_prefix ? 'Revoking...' : 'Revoke'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deleted users */}
      {deletedUsers.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <button onClick={() => setShowDeleted(!showDeleted)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--gray-500)', fontFamily: 'inherit' }}>
            {showDeleted ? '▼' : '▶'} Deactivated Users ({deletedUsers.length})
          </button>
          {showDeleted && (
            <div style={{ marginTop: 8, background: '#fff', borderRadius: 8, border: '1px solid var(--gray-200)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <tbody>
                  {deletedUsers.map(user => (
                    <tr key={user.admin_id} style={{ borderBottom: '1px solid var(--gray-200)', opacity: 0.6 }}>
                      <td style={{ padding: '10px 14px' }}>{user.staff_name}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--gray-500)' }}>{user.usern}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        <button onClick={() => restore(user.admin_id)} title="Restore"
                          style={{ ...BTN('var(--green)', false), padding: '4px 10px', fontSize: 11 }}>
                          <RotateCcw size={12} /> Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
